import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import Edit from 'assets/icons/EditBlue.png'
import Delete from 'assets/icons/Delete.png'
import { useState } from 'react'
import { PHONE_TYPES } from 'pages/address/address.constant'
import { contactAddFieldSx } from '../customer-detail-fields'
import {
  useDeleteCompanyPhoneMutation,
  useGetCompanyPhonesQuery,
  useUpdateCompanyPhoneMutation,
} from 'store/company/company.api'
import { CompanyAssociation, CompanyPhone, PhoneType } from 'store/company/company.types'
import { formatPhoneForDisplay, formatPhoneForSave } from 'utils/phone'

interface CustomerPhoneTabProps {
  association: CompanyAssociation
}

type PhoneEditDraft = {
  contactName: string
  phone: string
  phoneType: PhoneType
}

const CustomerPhoneTab = ({ association }: CustomerPhoneTabProps) => {
  const companyId = association.companyId
  const { data: phones = [], isLoading } = useGetCompanyPhonesQuery({
    companyId,
  })
  const [deletePhone, { isLoading: isDeleting }] = useDeleteCompanyPhoneMutation()
  const [updatePhone, { isLoading: isUpdating }] = useUpdateCompanyPhoneMutation()
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState<PhoneEditDraft | null>(null)

  const handleDelete = async (companyPhoneId: string) => {
    if (phones.length <= 1) {
      setError("Can't delete all phone numbers. At least one phone is required.")
      return
    }
    setError(null)
    try {
      await deletePhone({ companyPhoneId }).unwrap()
    } catch (e) {
      console.error('Failed to delete phone', e)
    }
  }

  const handleEdit = (row: CompanyPhone) => {
    setEditingId(row.companyPhoneId)
    setEditDraft({
      contactName: row.contactName ?? '',
      phone: formatPhoneForDisplay(row.phone),
      phoneType: row.phoneType,
    })
    setError(null)
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditDraft(null)
  }

  const handleSaveEdit = async (companyPhoneId: string) => {
    const trimmed = formatPhoneForSave(editDraft?.phone.trim() ?? '')
    if (!trimmed) return
    try {
      await updatePhone({
        companyPhoneId,
        body: {
          phone: trimmed,
          phoneType: editDraft?.phoneType ?? 'Dispatch',
          contactName: editDraft?.contactName.trim() ?? '',
        },
      }).unwrap()
      setEditingId(null)
      setEditDraft(null)
    } catch (e) {
      console.error('Failed to update phone', e)
    }
  }

  const formatPhoneType = (t: PhoneType) =>
    PHONE_TYPES.find((e) => e.value === t)?.label ?? t

  const isRowBusy = isDeleting || isUpdating

  return (
    <Box sx={{ px: 3, py: 2 }}>
      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {isLoading ? (
        <Typography variant="body2">Loading…</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Contact Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Phone Number</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {phones.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No phone numbers yet.
                </TableCell>
              </TableRow>
            ) : (
              phones.map((row) => {
                const isEditing = editingId === row.companyPhoneId

                return (
                  <TableRow key={row.companyPhoneId}>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editDraft?.contactName ?? ''}
                          onChange={(e) =>
                            setEditDraft((prev) =>
                              prev ? { ...prev, contactName: e.target.value } : prev
                            )
                          }
                          sx={contactAddFieldSx}
                        />
                      ) : (
                        row.contactName || '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          type="tel"
                          value={editDraft?.phone ?? ''}
                          onChange={(e) =>
                            setEditDraft((prev) =>
                              prev ? { ...prev, phone: e.target.value } : prev
                            )
                          }
                          sx={contactAddFieldSx}
                        />
                      ) : (
                        formatPhoneForDisplay(row.phone)
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editDraft?.phoneType ?? row.phoneType}
                            onChange={(e) =>
                              setEditDraft((prev) =>
                                prev
                                  ? { ...prev, phoneType: e.target.value as PhoneType }
                                  : prev
                              )
                            }
                            sx={contactAddFieldSx}
                          >
                            {PHONE_TYPES.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        formatPhoneType(row.phoneType)
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {isEditing ? (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleSaveEdit(row.companyPhoneId)}
                            disabled={isRowBusy || !editDraft?.phone.trim()}
                            aria-label="Save phone"
                          >
                            <CheckIcon fontSize="small" color="primary" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleCancelEdit}
                            disabled={isRowBusy}
                            aria-label="Cancel edit"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(row)}
                            disabled={isRowBusy || editingId !== null}
                            aria-label="Edit phone"
                          >
                            <img
                              src={Edit}
                              alt="Edit"
                              width={24}
                              height={24}
                              style={{ cursor: 'pointer' }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.companyPhoneId)}
                            disabled={isRowBusy || editingId !== null}
                            aria-label="Delete phone"
                          >
                            <img
                              src={Delete}
                              alt="Delete"
                              width={20}
                              height={22}
                              style={{ cursor: 'pointer' }}
                            />
                          </IconButton>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}

export default CustomerPhoneTab
